import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Clock, User, Save, Trash2, ChevronDown, ChevronUp, Download, Settings, BarChart3 } from 'lucide-react';

export default function BehaviorTracker() {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  
  const defaultBehaviors = [
    'Tantrums/Meltdowns',
    'Aggression (hitting, kicking)',
    'Verbal outbursts',
    'Non-compliance/Refusal',
    'Self-harm',
    'Withdrawal/Isolation',
    'Hyperactivity',
    'Difficulty focusing',
    'Anxiety/Worry',
    'Crying',
    'Property destruction',
    'Positive behavior'
  ];

  const defaultTriggers = [
    'Transition/Change in routine',
    'Denied request',
    'Academic task',
    'Social interaction',
    'Sensory stimuli (noise, lights)',
    'Hunger/Thirst',
    'Fatigue',
    'Overstimulation',
    'Boredom',
    'Physical discomfort',
    'Unknown'
  ];

  const defaultLocations = ['Home', 'School', 'Community', 'Therapy session', 'Other'];
  
  const defaultCoping = [
    'Deep breathing',
    'Time in quiet space',
    'Physical activity',
    'Verbal redirection',
    'Distraction technique',
    'Sensory tool (fidget, etc)',
    'Visual schedule',
    'Positive reinforcement',
    'Break time',
    'Other'
  ];

  const [behaviorOptions, setBehaviorOptions] = useState([]);
  const [triggerOptions, setTriggerOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [copingOptions, setCopingOptions] = useState([]);

  const intensityOptions = ['Mild', 'Moderate', 'Severe'];
  const durationOptions = ['< 5 minutes', '5-15 minutes', '15-30 minutes', '> 30 minutes'];

  const [currentEntry, setCurrentEntry] = useState({
    childName: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    behaviors: [],
    triggers: [],
    location: '',
    intensity: '',
    duration: '',
    antecedent: '',
    consequence: '',
    copingStrategies: [],
    notes: ''
  });

  const [editingBehaviors, setEditingBehaviors] = useState('');
  const [editingTriggers, setEditingTriggers] = useState('');
  const [editingLocations, setEditingLocations] = useState('');
  const [editingCoping, setEditingCoping] = useState('');

  useEffect(() => {
    const savedEntries = localStorage.getItem('behaviorEntries');
    if (savedEntries) {
      setEntries(JSON.parse(savedEntries));
    }

    const savedBehaviors = localStorage.getItem('customBehaviors');
    const savedTriggers = localStorage.getItem('customTriggers');
    const savedLocations = localStorage.getItem('customLocations');
    const savedCoping = localStorage.getItem('customCoping');

    setBehaviorOptions(savedBehaviors ? JSON.parse(savedBehaviors) : defaultBehaviors);
    setTriggerOptions(savedTriggers ? JSON.parse(savedTriggers) : defaultTriggers);
    setLocationOptions(savedLocations ? JSON.parse(savedLocations) : defaultLocations);
    setCopingOptions(savedCoping ? JSON.parse(savedCoping) : defaultCoping);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('behaviorEntries', JSON.stringify(entries));
  }, [entries]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  const handleCheckboxChange = (field, value) => {
    setCurrentEntry(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const saveEntry = () => {
    if (!currentEntry.childName || currentEntry.behaviors.length === 0) {
      alert('Please enter child name and select at least one behavior.');
      return;
    }

    const newEntry = {
      ...currentEntry,
      id: Date.now(),
      timestamp: new Date().toISOString()
    };

    setEntries([newEntry, ...entries]);
    resetForm();
    setShowForm(false);
  };

  const resetForm = () => {
    setCurrentEntry({
      childName: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      behaviors: [],
      triggers: [],
      location: '',
      intensity: '',
      duration: '',
      antecedent: '',
      consequence: '',
      copingStrategies: [],
      notes: ''
    });
  };

  const deleteEntry = (id) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      setEntries(entries.filter(entry => entry.id !== id));
    }
  };

  const exportData = () => {
    const headers = ['Date', 'Time', 'Child Name', 'Behaviors', 'Triggers', 'Location', 'Intensity', 'Duration', 'Antecedent', 'Consequence', 'Coping Strategies', 'Notes'];
    
    const rows = entries.map(entry => [
      entry.date || '',
      entry.time || '',
      entry.childName || '',
      entry.behaviors.join('; ') || '',
      entry.triggers.join('; ') || '',
      entry.location || '',
      entry.intensity || '',
      entry.duration || '',
      entry.antecedent || '',
      entry.consequence || '',
      entry.copingStrategies.join('; ') || '',
      entry.notes || ''
    ]);

    const escapeCsvCell = (cell) => {
      const cellStr = String(cell);
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') || cellStr.includes(';')) {
        return '"' + cellStr.replace(/"/g, '""') + '"';
      }
      return cellStr;
    };

    let csvContent = '\uFEFF';
    csvContent += headers.map(escapeCsvCell).join(',') + '\r\n';
    
    rows.forEach(row => {
      csvContent += row.map(escapeCsvCell).join(',') + '\r\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `behavior-log-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openAdmin = () => {
    setEditingBehaviors(behaviorOptions.join('\n'));
    setEditingTriggers(triggerOptions.join('\n'));
    setEditingLocations(locationOptions.join('\n'));
    setEditingCoping(copingOptions.join('\n'));
    setShowAdmin(true);
  };

  const saveAdminChanges = () => {
    const newBehaviors = editingBehaviors.split('\n').filter(b => b.trim());
    const newTriggers = editingTriggers.split('\n').filter(t => t.trim());
    const newLocations = editingLocations.split('\n').filter(l => l.trim());
    const newCoping = editingCoping.split('\n').filter(c => c.trim());

    setBehaviorOptions(newBehaviors);
    setTriggerOptions(newTriggers);
    setLocationOptions(newLocations);
    setCopingOptions(newCoping);

    localStorage.setItem('customBehaviors', JSON.stringify(newBehaviors));
    localStorage.setItem('customTriggers', JSON.stringify(newTriggers));
    localStorage.setItem('customLocations', JSON.stringify(newLocations));
    localStorage.setItem('customCoping', JSON.stringify(newCoping));

    setShowAdmin(false);
    alert('Settings saved! New options will appear in the form.');
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset all options to defaults? This cannot be undone.')) {
      setBehaviorOptions(defaultBehaviors);
      setTriggerOptions(defaultTriggers);
      setLocationOptions(defaultLocations);
      setCopingOptions(defaultCoping);

      localStorage.setItem('customBehaviors', JSON.stringify(defaultBehaviors));
      localStorage.setItem('customTriggers', JSON.stringify(defaultTriggers));
      localStorage.setItem('customLocations', JSON.stringify(defaultLocations));
      localStorage.setItem('customCoping', JSON.stringify(defaultCoping));

      setEditingBehaviors(defaultBehaviors.join('\n'));
      setEditingTriggers(defaultTriggers.join('\n'));
      setEditingLocations(defaultLocations.join('\n'));
      setEditingCoping(defaultCoping.join('\n'));

      alert('Reset to defaults!');
    }
  };

  const getChartData = () => {
    if (entries.length === 0) return null;

    const behaviorCounts = {};
    entries.forEach(entry => {
      entry.behaviors.forEach(behavior => {
        behaviorCounts[behavior] = (behaviorCounts[behavior] || 0) + 1;
      });
    });

    const timeSlots = {
      'Morning (6am-12pm)': 0,
      'Afternoon (12pm-5pm)': 0,
      'Evening (5pm-9pm)': 0,
      'Night (9pm-6am)': 0
    };

    entries.forEach(entry => {
      const hour = parseInt(entry.time.split(':')[0]);
      if (hour >= 6 && hour < 12) timeSlots['Morning (6am-12pm)']++;
      else if (hour >= 12 && hour < 17) timeSlots['Afternoon (12pm-5pm)']++;
      else if (hour >= 17 && hour < 21) timeSlots['Evening (5pm-9pm)']++;
      else timeSlots['Night (9pm-6am)']++;
    });

    const intensityCounts = {};
    entries.forEach(entry => {
      if (entry.intensity) {
        intensityCounts[entry.intensity] = (intensityCounts[entry.intensity] || 0) + 1;
      }
    });

    const triggerCounts = {};
    entries.forEach(entry => {
      entry.triggers.forEach(trigger => {
        triggerCounts[trigger] = (triggerCounts[trigger] || 0) + 1;
      });
    });

    return {
      behaviorCounts,
      timeSlots,
      intensityCounts,
      triggerCounts,
      totalEntries: entries.length
    };
  };

  const chartData = getChartData();

  const BarChartComponent = ({ data, title, color }) => {
    if (!data || Object.keys(data).length === 0) return null;

    const maxValue = Math.max(...Object.values(data));
    const sortedData = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const total = Object.values(data).reduce((sum, val) => sum + val, 0);

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{title}</h3>
        <div className="space-y-3">
          {sortedData.map(([label, value]) => {
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            const barWidth = maxValue > 0 ? (value / maxValue) * 100 : 0;
            
            return (
              <div key={label} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{label}</span>
                  <span className="text-gray-600 font-semibold">{value} ({percentage}%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6">
                  <div
                    className="h-6 rounded-full flex items-center justify-end pr-2 text-white text-xs font-semibold transition-all duration-300"
                    style={{ 
                      width: `${Math.max(barWidth, value > 0 ? 5 : 0)}%`,
                      backgroundColor: color,
                      minWidth: value > 0 ? '30px' : '0'
                    }}
                  >
                    {value > 0 && value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const PieChartComponent = ({ data, title }) => {
    if (!data || Object.keys(data).length === 0) return null;

    const total = Object.values(data).reduce((sum, val) => sum + val, 0);
    if (total === 0) return null;
    
    const colors = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];
    
    let currentAngle = 0;
    const slices = Object.entries(data).map(([label, value], index) => {
      const percentage = ((value / total) * 100).toFixed(1);
      const angle = (value / total) * 360;
      const slice = {
        label,
        value,
        percentage,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        color: colors[index % colors.length]
      };
      currentAngle += angle;
      return slice;
    });

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{title}</h3>
        <div className="flex flex-col md:flex-row items-center justify-center gap-8">
          <div className="relative w-64 h-64 flex-shrink-0">
            <svg viewBox="0 0 100 100" className="transform -rotate-90 w-full h-full">
              {slices.map((slice, index) => {
                const startRad = (slice.startAngle * Math.PI) / 180;
                const endRad = (slice.endAngle * Math.PI) / 180;
                const x1 = 50 + 40 * Math.cos(startRad);
                const y1 = 50 + 40 * Math.sin(startRad);
                const x2 = 50 + 40 * Math.cos(endRad);
                const y2 = 50 + 40 * Math.sin(endRad);
                const largeArc = slice.endAngle - slice.startAngle > 180 ? 1 : 0;

                return (
                  <path
                    key={index}
                    d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                    fill={slice.color}
                    stroke="white"
                    strokeWidth="0.5"
                  />
                );
              })}
            </svg>
          </div>
          <div className="flex-1 space-y-3 min-w-0">
            {slices.map((slice, index) => (
              <div key={index} className="flex items-center gap-3">
                <div
                  className="w-4 h-4 rounded flex-shrink-0"
                  style={{ backgroundColor: slice.color }}
                ></div>
                <span className="text-sm text-gray-700 flex-1 min-w-0">{slice.label}</span>
                <span className="text-sm font-semibold text-gray-600 flex-shrink-0">
                  {slice.value} ({slice.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const EntryCard = ({ entry, onDelete }) => {
    const [expanded, setExpanded] = useState(false);

    return (
      <div className="bg-white rounded-lg shadow-md p-4 mb-4 border-l-4" style={{ borderLeftColor: '#45B08C' }}>
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-gray-600" />
              <span className="font-semibold text-lg">{entry.childName}</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {entry.date}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {entry.time}
              </div>
            </div>
          </div>
          <button
            onClick={() => onDelete(entry.id)}
            className="text-red-500 hover:text-red-700 p-2"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-3">
          <div className="font-semibold text-sm text-gray-700 mb-2">Behaviors:</div>
          <div className="flex flex-col gap-2 mb-4">
            {entry.behaviors.map((b, i) => (
              <span key={i} className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm inline-block">
                {b}
              </span>
            ))}
          </div>
        </div>

        {entry.intensity && (
          <div className="mb-4">
            <span className={`inline-block px-3 py-1 rounded text-sm font-semibold ${
              entry.intensity === 'Severe' ? 'bg-red-200 text-red-900' :
              entry.intensity === 'Moderate' ? 'bg-yellow-200 text-yellow-900' :
              'bg-green-200 text-green-900'
            }`}>
              {entry.intensity} Intensity
            </span>
          </div>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-sm font-medium"
          style={{ color: '#45B08C' }}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? 'Show less' : 'Show details'}
        </button>

        {expanded && (
          <div className="mt-4 space-y-3 pt-3 border-t">
            {entry.triggers.length > 0 && (
              <div>
                <div className="font-semibold text-sm text-gray-700 mb-1">Triggers:</div>
                <div className="flex flex-col gap-2">
                  {entry.triggers.map((t, i) => (
                    <span key={i} className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-sm inline-block">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {entry.location && (
              <div>
                <span className="font-semibold text-sm text-gray-700">Location: </span>
                <span className="text-sm">{entry.location}</span>
              </div>
            )}

            {entry.duration && (
              <div>
                <span className="font-semibold text-sm text-gray-700">Duration: </span>
                <span className="text-sm">{entry.duration}</span>
              </div>
            )}

            {entry.antecedent && (
              <div>
                <div className="font-semibold text-sm text-gray-700 mb-1">What happened before:</div>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{entry.antecedent}</p>
              </div>
            )}

            {entry.consequence && (
              <div>
                <div className="font-semibold text-sm text-gray-700 mb-1">What happened after:</div>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{entry.consequence}</p>
              </div>
            )}

            {entry.copingStrategies.length > 0 && (
              <div>
                <div className="font-semibold text-sm text-gray-700 mb-1">Coping strategies used:</div>
                <div className="flex flex-col gap-2">
                  {entry.copingStrategies.map((c, i) => (
                    <span key={i} className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm inline-block">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {entry.notes && (
              <div>
                <div className="font-semibold text-sm text-gray-700 mb-1">Additional notes:</div>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{entry.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen p-4" style={{ background: 'linear-gradient(to bottom right, #e0f2f1, #b2dfdb)' }}>
      <div className="max-w-4xl mx-auto" style={{ paddingLeft: '1in', paddingRight: '1in' }}>
        {showInstallButton && (
          <div className="text-white rounded-lg shadow-lg p-4 mb-4 flex items-center justify-between" style={{ backgroundColor: '#45B08C' }}>
            <div>
              <p className="font-semibold">Install this app on your device</p>
              <p className="text-sm opacity-90">Access it anytime, even offline!</p>
            </div>
            <button
              onClick={handleInstallClick}
              className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg font-semibold"
              style={{ color: '#45B08C' }}
            >
              <Download className="w-5 h-5" />
              Install
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">BEAM</h1>
              <p className="text-sm text-gray-600 mt-1">Behavior Evaluation And Monitoring</p>
            </div>
            <button
              onClick={openAdmin}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 hover:bg-gray-50"
              style={{ borderColor: '#45B08C', color: '#45B08C' }}
              title="Therapist Settings"
            >
              <Settings className="w-5 h-5" />
              Settings
            </button>
          </div>
          <p className="text-gray-600 mb-4">
            Track behaviors throughout the day to help identify patterns and triggers for therapy review.
          </p>
          
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 text-white px-6 py-3 rounded-lg font-semibold shadow-md"
              style={{ backgroundColor: '#45B08C' }}
            >
              <Plus className="w-5 h-5" />
              New Entry
            </button>
            
            {entries.length > 0 && (
              <>
                <button
                  onClick={() => setShowCharts(!showCharts)}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-md border-2"
                  style={{ borderColor: '#45B08C', color: '#45B08C', backgroundColor: 'white' }}
                >
                  <BarChart3 className="w-5 h-5" />
                  {showCharts ? 'Hide' : 'View'} Analytics
                </button>
                <button
                  onClick={exportData}
                  className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold shadow-md"
                >
                  <Save className="w-5 h-5" />
                  Export to Excel
                </button>
              </>
            )}
          </div>
        </div>

        {showCharts && chartData && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Behavior Analytics</h2>
              <p className="text-gray-600 mb-4">
                Based on {chartData.totalEntries} total entries
              </p>
            </div>

            <BarChartComponent 
              data={chartData.behaviorCounts} 
              title="Most Frequent Behaviors" 
              color="#8b5cf6"
            />

            <PieChartComponent 
              data={chartData.timeSlots} 
              title="Time of Day Distribution"
            />

            <BarChartComponent 
              data={chartData.triggerCounts} 
              title="Common Triggers" 
              color="#f59e0b"
            />

            {Object.keys(chartData.intensityCounts).length > 0 && (
              <BarChartComponent 
                data={chartData.intensityCounts} 
                title="Intensity Levels" 
                color="#ef4444"
              />
            )}
          </div>
        )}

        {showAdmin && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border-2" style={{ borderColor: '#45B08C' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Therapist Settings</h2>
            <p className="text-gray-600 mb-4">Customize options for specific children. Each option on a new line.</p>
            
            <div className="space-y-6">
              <div>
                <label className="block font-semibold text-gray-700 mb-2">Behavior Options</label>
                <textarea
                  value={editingBehaviors}
                  onChange={(e) => setEditingBehaviors(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:border-transparent font-mono text-sm"
                  rows="8"
                  placeholder="One behavior per line..."
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-2">Trigger Options</label>
                <textarea
                  value={editingTriggers}
                  onChange={(e) => setEditingTriggers(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:border-transparent font-mono text-sm"
                  rows="6"
                  placeholder="One trigger per line..."
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-2">Location Options</label>
                <textarea
                  value={editingLocations}
                  onChange={(e) => setEditingLocations(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:border-transparent font-mono text-sm"
                  rows="4"
                  placeholder="One location per line..."
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-2">Coping Strategy Options</label>
                <textarea
                  value={editingCoping}
                  onChange={(e) => setEditingCoping(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:border-transparent font-mono text-sm"
                  rows="6"
                  placeholder="One coping strategy per line..."
                />
              </div>

              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={saveAdminChanges}
                  className="flex items-center gap-2 text-white px-6 py-3 rounded-lg font-semibold shadow-md"
                  style={{ backgroundColor: '#45B08C' }}
                >
                  <Save className="w-5 h-5" />
                  Save Changes
                </button>
                <button
                  onClick={() => setShowAdmin(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={resetToDefaults}
                  className="px-6 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-semibold"
                >
                  Reset to Defaults
                </button>
              </div>
            </div>
          </div>
        )}

        {showForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Log Behavior Entry</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block font-bold text-gray-800 mb-3 text-base">
                  Child's Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={currentEntry.childName}
                  onChange={(e) => setCurrentEntry({ ...currentEntry, childName: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  placeholder="Enter child's name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-gray-800 mb-3 text-base">Date</label>
                  <input
                    type="date"
                    value={currentEntry.date}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, date: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-800 mb-3 text-base">Time</label>
                  <input
                    type="time"
                    value={currentEntry.time}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, time: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-gray-800 mb-4 text-base">
                  Behaviors Observed <span className="text-red-500">*</span>
                </label>
                <div className="w-full space-y-4">
                  {behaviorOptions.map(behavior => (
                    <div key={behavior} className="block w-full">
                      <label className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer w-full transition-colors">
                        <input
                          type="checkbox"
                          checked={currentEntry.behaviors.includes(behavior)}
                          onChange={() => handleCheckboxChange('behaviors', behavior)}
                          className="w-5 h-5 rounded focus:ring-2 mt-0.5 flex-shrink-0"
                          style={{ accentColor: '#45B08C' }}
                        />
                        <span className="text-base leading-relaxed">{behavior}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block font-bold text-gray-800 mb-4 text-base">Possible Triggers</label>
                <div className="w-full space-y-4">
                  {triggerOptions.map(trigger => (
                    <div key={trigger} className="block w-full">
                      <label className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer w-full transition-colors">
                        <input
                          type="checkbox"
                          checked={currentEntry.triggers.includes(trigger)}
                          onChange={() => handleCheckboxChange('triggers', trigger)}
                          className="w-5 h-5 rounded focus:ring-2 mt-0.5 flex-shrink-0"
                          style={{ accentColor: '#45B08C' }}
                        />
                        <span className="text-base leading-relaxed">{trigger}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block font-bold text-gray-800 mb-3 text-base">Location</label>
                  <select
                    value={currentEntry.location}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, location: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  >
                    <option value="">Select location</option>
                    {locationOptions.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-gray-800 mb-3 text-base">Intensity</label>
                  <select
                    value={currentEntry.intensity}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, intensity: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  >
                    <option value="">Select intensity</option>
                    {intensityOptions.map(int => (
                      <option key={int} value={int}>{int}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-gray-800 mb-3 text-base">Duration</label>
                  <select
                    value={currentEntry.duration}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, duration: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  >
                    <option value="">Select duration</option>
                    {durationOptions.map(dur => (
                      <option key={dur} value={dur}>{dur}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-gray-800 mb-3 text-base">
                  What happened immediately before? (Antecedent)
                </label>
                <textarea
                  value={currentEntry.antecedent}
                  onChange={(e) => setCurrentEntry({ ...currentEntry, antecedent: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  rows="3"
                  placeholder="Describe what was happening right before the behavior occurred..."
                />
              </div>

              <div>
                <label className="block font-bold text-gray-800 mb-3 text-base">
                  What happened after? (Consequence)
                </label>
                <textarea
                  value={currentEntry.consequence}
                  onChange={(e) => setCurrentEntry({ ...currentEntry, consequence: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  rows="3"
                  placeholder="Describe the response or outcome after the behavior..."
                />
              </div>

              <div>
                <label className="block font-bold text-gray-800 mb-4 text-base">
                  Coping Strategies Used
                </label>
                <div className="w-full space-y-4">
                  {copingOptions.map(coping => (
                    <div key={coping} className="block w-full">
                      <label className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer w-full transition-colors">
                        <input
                          type="checkbox"
                          checked={currentEntry.copingStrategies.includes(coping)}
                          onChange={() => handleCheckboxChange('copingStrategies', coping)}
                          className="w-5 h-5 rounded focus:ring-2 mt-0.5 flex-shrink-0"
                          style={{ accentColor: '#45B08C' }}
                        />
                        <span className="text-base leading-relaxed">{coping}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block font-bold text-gray-800 mb-3 text-base">
                  Additional Notes
                </label>
                <textarea
                  value={currentEntry.notes}
                  onChange={(e) => setCurrentEntry({ ...currentEntry, notes: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  rows="4"
                  placeholder="Any other relevant observations, context, or details..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={saveEntry}
                  className="flex items-center gap-2 text-white px-6 py-3 rounded-lg font-semibold"
                  style={{ backgroundColor: '#45B08C' }}
                >
                  <Save className="w-5 h-5" />
                  Save Entry
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Recent Entries ({entries.length})
          </h2>
          
          {entries.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-500">No entries yet. Click "New Entry" to start tracking behaviors.</p>
            </div>
          ) : (
            <div>
              {entries.map(entry => (
                <EntryCard key={entry.id} entry={entry} onDelete={deleteEntry} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}